import { Box, Grid2 } from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import CustomChip from '@ui-components/Chip/CustomChip';
import CustomDivider from '@ui-components/Divider/CustomDivider';
import CustomLink from '@ui-components/Link/CustomLink';
import CustomMenu from '@ui-components/Menu/CustomMenu';
import CustomMenuItem from '@ui-components/Menu/CustomMenuItem';
import CustomMenuList from '@ui-components/Menu/CustomMenuList';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomListItemIcon from '@ui-components/List/CustomListItemIcon';
import CustomBadge from '@ui-components/Badge/CustomBadge';
import MetricsCount from './MetricsCount';
import MetricCard from './MetricCard';
// Icons
import FilterIcon from '@assets/images/Filter.svg';
import SortIcon from '@assets/images/Sort.svg';
import CheckIcon from '@assets/images/check.svg';
import ArrowDown from '@assets/images/arrow-down.svg';
import ArrowUp from '@assets/images/arrow-up.svg';
import {
  useGetMetricsData,
  useGetFacilityFilterMetrics,
} from '../../API/hooks/metricsHook';
import type {
  MetricCardData,
  MetricsResponse,
} from '../../API/services/metricsService';
import type {
  // Facility,
  IndeterminateCheckboxProps,
} from '../../API/services/consultsService';
import LoaderComponent from '../components/LoaderComponent';
import moment from 'moment';
import FilterCheckbox from '../components/FilterCheckbox';
import CustomTextField from '@ui-components/TextField/CustomTextField';

const FacilityMetrics: React.FC = () => {
  const { metricsData, isPending } = useGetMetricsData();
  const updatedData = {
    ...metricsData,
    metricCardData: metricsData?.metricCardData.map((facility) => {
      const newAcceptData = [
        ...facility.donutChart.acceptData,
        {
          label: '',
          value:
            facility.totalRequest - facility.donutChart.acceptData[0].value,
        },
      ];

      return {
        ...facility,
        donutChart: {
          ...facility.donutChart,
          acceptData: newAcceptData,
        },
      };
    }),
  };

  const [filterMetricsData, setFilterMetricsData] =
    useState<MetricsResponse | null>(null);
  const checkedIdsApply = useRef<number[]>([]);
  const { facilityFilter } = useGetFacilityFilterMetrics();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [selectedFacilityIdBadge, setSelectedFacilityIdBadge] = useState<
    number | null
  >(0);
  const [clearFilter, setClearFilter] = useState(false);
  const open = Boolean(anchorEl);
  const [showBadge, setShowBadge] = useState(false);

  const [anchorElFacilities, setAnchorElFacilities] =
    useState<null | HTMLElement>(null);
  const [openFacilities, setOpenFacilities] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseFacilities = () => {
    setAnchorElFacilities(null);
    setOpenFacilities(false);
  };

  const [selectedFont, setSelectedFont] = useState('atoz');

  useEffect(() => {
    if (metricsData) {
      setFilterMetricsData(structuredClone(updatedData) as MetricsResponse);
    }
  }, [metricsData]);

  const sortingMetricsDataForList = (
    metricsData: MetricCardData[],
    sort: string
  ): MetricCardData[] => {
    if (metricsData) {
      switch (sort) {
        case 'atoz':
          metricsData.sort((a, b) => {
            const nameA = a.facilityName ?? '';
            const nameB = b.facilityName ?? '';
            return nameA.localeCompare(nameB);
          });
          break;

        case 'ztoa':
          metricsData.sort((a, b) => {
            const nameA = a.facilityName ?? '';
            const nameB = b.facilityName ?? '';
            return nameB.localeCompare(nameA);
          });
          break;

        case 'reqdesc':
          metricsData.sort((a, b) => {
            const reqA = a.totalRequest ?? 0;
            const reqB = b.totalRequest ?? 0;
            return reqB - reqA;
          });
          break;

        case 'reqasc':
          metricsData.sort((a, b) => {
            const reqA = a.totalRequest ?? 0;
            const reqB = b.totalRequest ?? 0;
            return reqA - reqB;
          });
          break;

        default:
          break;
      }
    }
    return metricsData || [];
  };

  const sortingAndFiltering = (
    sort: string,
    ids: IndeterminateCheckboxProps[] | null
  ) => {
    console.log('ids', ids);
    if (!metricsData) return;
    let filteredAndSortedMetricsData: MetricCardData[] = [];

    const cloneMetricsData = structuredClone(updatedData) as MetricsResponse;

    if (ids && ids.length > 0) {
      filteredAndSortedMetricsData = filterMetrics(ids, cloneMetricsData);
    } else {
      filteredAndSortedMetricsData = cloneMetricsData.metricCardData ?? [];
    }

    // Apply sorting on the filtered data
    filteredAndSortedMetricsData = sortingMetricsDataForList(
      filteredAndSortedMetricsData,
      sort
    );

    setSelectedFont(sort);
    handleClose();

    if (filterMetricsData) {
      setFilterMetricsData({
        ...filterMetricsData,
        metricCardData: filteredAndSortedMetricsData,
      });
    }
  };

  const [showSortBadge, setShowSortBadge] = useState(false);

  // Filters
  const [searchHospitalKey, setSearchHospitalKey] = useState<string>('');
  const [facilityCount, setFacilityCount] = useState(0);
  const checkedIdRef = useRef<IndeterminateCheckboxProps[]>([]);
  const [filterSelectedIds, setFilterSelectedIds] = useState<
    IndeterminateCheckboxProps[]
  >([]);

  const handleClickFacilities = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElFacilities(event.currentTarget);
    setOpenFacilities(true);
    let count = 0;

    checkedIdRef.current?.forEach((facility) => {
      if (
        facility.checkFacility ||
        facility.hospitals.some((hospital) => hospital.checked)
      ) {
        count++;
      }
    });

    setFacilityCount(count);
  };

  const getFacilityCountDisplay = (shouldClear: boolean, count: number) => {
    if (shouldClear) return 0;
    return count;
  };

  const getFacilityCount = (data: IndeterminateCheckboxProps[]) => {
    let count = 0;
    data.forEach((facility) => {
      if (
        facility.checkFacility ||
        facility.hospitals.some((hospital) => hospital.checked)
      ) {
        count++;
      }
    });

    setFacilityCount(count);
  };

  const handleHospitalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchHospitalKey(event.target.value);
  };

  const filterByFacility = (
    facilityName: string,
    metricsData: MetricsResponse
  ): MetricCardData[] => {
    return (
      metricsData?.metricCardData.filter(
        (item) => item.partnerName === facilityName
      ) ?? []
    );
  };

  const filterByHospital = (
    hospitalName: string,
    metricsData: MetricsResponse
  ): MetricCardData[] => {
    return (
      metricsData?.metricCardData.filter(
        (item) => item.facilityName === hospitalName
      ) ?? []
    );
  };

  const processCheckedHospitals = (
    firstItem: IndeterminateCheckboxProps,
    metricsData: MetricsResponse
  ): MetricCardData[] => {
    const results: MetricCardData[] = [];
    firstItem.hospitals.forEach((element) => {
      if (element.checked) {
        const matchedObjects = filterByHospital(element.name, metricsData);
        results.push(...matchedObjects);
      }
    });
    return results;
  };

  const filterMetrics = (
    ids: IndeterminateCheckboxProps[],
    metricsData: MetricsResponse
  ): MetricCardData[] => {
    const filterMetricsData: MetricCardData[] = [];

    if (metricsData && ids && ids.length > 0) {
      ids.forEach((firstItem) => {
        if (firstItem.checkFacility) {
          const matchedObjects = filterByFacility(
            firstItem.faciltyName,
            metricsData
          );
          filterMetricsData.push(...matchedObjects);
        } else if (firstItem.checkInderterminate) {
          const matchedObjects = processCheckedHospitals(
            firstItem,
            metricsData
          );
          filterMetricsData.push(...matchedObjects);
        }
      });
    }
    console.log('filteredData', filterMetricsData);
    return filterMetricsData;
  };

  const handleApplyFilter = (checkedIds: IndeterminateCheckboxProps[]) => {
    if (!metricsData) return;
    setFilterSelectedIds(checkedIds);
    sortingAndFiltering(selectedFont, checkedIds);
  };

  const handleClearFilter = () => {
    checkedIdsApply.current = [];
    sortingAndFiltering(selectedFont, []);
    setFilterSelectedIds([]);
    setClearFilter(true);
    handleCloseFacilities();
    setShowBadge(false);
  };

  const handleSortingChange = (sort: string) => {
    setSelectedFont(sort); // Update sorting state
    sortingAndFiltering(sort, filterSelectedIds);
  };

  return (
    <Box>
      <Box>
        <LoaderComponent isOpen={isPending} />
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }} className="flex">
            <Box className="flex text-align-left align-items-center ">
              <CustomTypography
                id="title-popup"
                variant="body2"
                color="textSecondary"
                className="pr-12"
              >
                Last Updated On:
              </CustomTypography>
              <CustomTypography
                id="title-popup"
                variant="body2"
                color="textPrimary"
              >
                {moment
                  .utc(metricsData?.lastUpdated)
                  .local()
                  .format('MMM D, YYYY - HH:mm')}
              </CustomTypography>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
            <Box className="flex float-right">
              <Box className="mr-16">
                <CustomBadge
                  id="facility-filter-badge"
                  badgeContent={showSortBadge ? '' : null}
                  color="error"
                  overlap="circular"
                >
                  <CustomChip
                    aria-controls={open ? 'fade-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={(e) => handleClick(e)}
                    className="box-border2"
                    id="sort-filter"
                    label="Sort"
                    variant="outlined"
                    icon={<SortIcon />}
                    iconPosition="left"
                  ></CustomChip>
                </CustomBadge>

                <CustomMenu
                  id="conditional-menu"
                  anchorEl={anchorEl ?? document.body}
                  open={open}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  className="mt-4"
                >
                  <Box className="status-filter-width p-20">
                    <Box className="flex-justify-space-between align-items-center">
                      <CustomTypography id="small-font" variant="subtitle1">
                        Sort By
                      </CustomTypography>
                      <CustomLink
                        className="float-right"
                        color="primary"
                        underline="hover"
                        onClick={() => {
                          handleClose();
                          handleSortingChange('atoz');
                          setShowSortBadge(false);
                        }}
                        id="reset-sortby"
                      >
                        Reset
                      </CustomLink>
                    </Box>
                    <Box className="pt-12 pb-10">
                      <CustomDivider />
                    </Box>
                    <CustomMenuList>
                      <CustomMenuItem
                        className="sorting-item"
                        onClick={() => {
                          handleSortingChange('atoz');
                          setShowSortBadge(false);
                        }}
                      >
                        <CustomTypography
                          id="small-font"
                          variant="h6"
                          className="pr-24"
                        >
                          {'A -> Z'}
                        </CustomTypography>
                        <CustomListItemIcon className="float-right">
                          {selectedFont.toLocaleLowerCase() === 'atoz' && (
                            <CheckIcon />
                          )}
                        </CustomListItemIcon>
                      </CustomMenuItem>
                      <CustomMenuItem
                        className="sorting-item"
                        onClick={() => {
                          handleSortingChange('ztoa');
                          setShowSortBadge(true);
                        }}
                      >
                        <CustomTypography
                          id="small-font"
                          variant="h6"
                          className="pr-24"
                        >
                          {'Z -> A'}
                        </CustomTypography>
                        <CustomListItemIcon className="float-right">
                          {selectedFont.toLocaleLowerCase() === 'ztoa' && (
                            <CheckIcon />
                          )}
                        </CustomListItemIcon>
                      </CustomMenuItem>
                      <CustomMenuItem
                        className="sorting-item"
                        onClick={() => {
                          handleSortingChange('reqasc');
                          setShowSortBadge(true);
                        }}
                      >
                        <CustomTypography
                          id="small-font"
                          variant="h6"
                          className="pr-24"
                        >
                          {'Requests'}&nbsp;
                          <ArrowUp />
                        </CustomTypography>
                        <CustomListItemIcon className="float-right">
                          {selectedFont.toLocaleLowerCase() === 'reqasc' && (
                            <CheckIcon />
                          )}
                        </CustomListItemIcon>
                      </CustomMenuItem>
                      <CustomMenuItem
                        className="sorting-item"
                        onClick={() => {
                          handleSortingChange('reqdesc');
                          setShowSortBadge(true);
                        }}
                      >
                        <CustomTypography
                          id="small-font"
                          variant="h6"
                          className="pr-24"
                        >
                          {'Requests'}&nbsp;
                          <ArrowDown />
                        </CustomTypography>
                        <CustomListItemIcon className="float-right">
                          {selectedFont.toLocaleLowerCase() === 'reqdesc' && (
                            <CheckIcon />
                          )}
                        </CustomListItemIcon>
                      </CustomMenuItem>
                    </CustomMenuList>
                  </Box>
                </CustomMenu>
              </Box>
              <Box>
                {showBadge && (
                  <CustomBadge
                    id="facility-filter-badge"
                    badgeContent={selectedFacilityIdBadge}
                    color="error"
                    max={600}
                    className="mr-10"
                  >
                    <CustomChip
                      aria-controls={
                        openFacilities ? 'fade-menu-facilities' : undefined
                      }
                      aria-haspopup="true"
                      aria-expanded={openFacilities ? 'true' : undefined}
                      onClick={(e) => handleClickFacilities(e)}
                      className="box-border2"
                      id="demo-chip-facilities"
                      label="Facilities"
                      icon={<FilterIcon />}
                      iconPosition="left"
                      variant="outlined"
                    />
                  </CustomBadge>
                )}
                {!showBadge && (
                  <CustomChip
                    aria-controls={
                      openFacilities ? 'fade-menu-facilities' : undefined
                    }
                    aria-haspopup="true"
                    aria-expanded={openFacilities ? 'true' : undefined}
                    onClick={(e) => handleClickFacilities(e)}
                    className="box-border2"
                    id="demo-chip-facilities"
                    label="Facilities"
                    icon={<FilterIcon />}
                    iconPosition="left"
                    variant="outlined"
                  />
                )}
                {Boolean(anchorElFacilities && openFacilities) && (
                  <CustomMenu
                    id="hospitallistmenu-menu"
                    anchorEl={anchorElFacilities}
                    open={openFacilities}
                    onClose={handleCloseFacilities}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    className="mt-4"
                  >
                    <Box className="mr-24 ml-24 mt-16 mb-16">
                      <Box className="flex-justify-space-between align-items-center">
                        <CustomTypography
                          id="small-font"
                          variant="subtitle1"
                          className="pr-24"
                        >
                          Facilities&nbsp;(
                          {getFacilityCountDisplay(clearFilter, facilityCount)})
                        </CustomTypography>
                        <CustomLink
                          className="float-right"
                          color="primary"
                          underline="hover"
                          onClick={handleClearFilter}
                          id="metrics-clear-filter"
                        >
                          Clear Filter
                        </CustomLink>
                      </Box>
                      <Box className="pt-18">
                        <CustomDivider />
                      </Box>
                      <Box className="pt-16">
                        <CustomTextField
                          id="txtSearchHospitalName"
                          placeholder="Search"
                          size="small"
                          type="text"
                          className="mr-10 searchHeight"
                          value={searchHospitalKey}
                          onChange={handleHospitalChange}
                        />
                      </Box>
                    </Box>
                    <CustomMenuList>
                      {facilityFilter && (
                        <FilterCheckbox
                          searchText={searchHospitalKey}
                          applyButtonCliked={(checkedIdsData) => {
                            setFilterSelectedIds(checkedIdsData);
                            checkedIdRef.current = checkedIdsData;
                            setClearFilter(false);
                            handleCloseFacilities();
                            setShowBadge(true);
                            setSelectedFacilityIdBadge(
                              checkedIdsData?.length ?? null
                            );

                            handleApplyFilter(checkedIdRef.current);
                          }}
                          healthSystems={facilityFilter.healthSystems}
                          clearFilter={clearFilter}
                          getFacilityCount={getFacilityCount}
                          storedPreviousValues={checkedIdRef.current}
                        />
                      )}
                    </CustomMenuList>
                  </CustomMenu>
                )}
              </Box>
            </Box>
          </Grid2>
        </Grid2>
        <Box className="pt-24">
          <MetricsCount metricsCountData={filterMetricsData} />
        </Box>
        <Box className="pt-24">
          <MetricCard
            metricsChartData={metricsData ?? null}
            selectedFont={selectedFont}
            filteredMetricsData={filterMetricsData?.metricCardData}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default FacilityMetrics;
